import { c as _c } from "react/compiler-runtime";
import { Box, Grid, Heading, Stack, Text } from '@chakra-ui/react';
import type { ScoProduct } from '@src/graphql-types';
import type { ReactNode } from 'react';
import type { PaywallCardAppearance } from './PaywallProductCard';
import { PaywallProductGrid } from './PaywallProductGrid';
import { PlanChoiceButton } from './PlanChoiceButton';
import { ProductDetailComparisonTable, type PaywallComparisonBaseline, type PaywallComparisonRow } from './ProductDetailComparisonTable';
import { orderProductsForIntent, resolvePaywallIntent, type PaywallIntentId } from './intent';
import { billingTermChangeLabel } from './offers';
import { productCanContinue, productCtaLabel, productDescription, productLabel, productOfferState, productPaywallIntent, productPriceLabel } from './presentation';
export type PaywallAnswer = {
  question: string;
  answer: string;
};
export type PaywallPageProps = {
  products: ScoProduct[];
  cardAppearance?: PaywallCardAppearance;
  intent?: PaywallIntentId;
  tokens?: Record<string, string>;
  ctaLabels?: Record<string, string>;
  currentProductIds?: string[];
  /** Billing term already paid, keyed by productId. See `productOfferState`. */
  currentTermByProductId?: Record<string, string | null>;
  eligibleProductIds?: string[];
  answers?: PaywallAnswer[];
  /** Offer controls (billing term, promotion code), placed under the hero, above the cards they price. */
  controls?: ReactNode;
  /** Under the cards, above the compare: offer controls only some visitors need (a promotion code field). */
  cardsFooter?: ReactNode;
  /** Off when the page already has a section heading for the plans (landing pages). */
  showHero?: boolean;
  /** `center` centers the cards under a centered section heading. */
  align?: 'start' | 'center';
  /** A secondary way forward inside the closing offer, e.g. "Book a call". */
  closingExtra?: ReactNode;
  /** Caller-verified reassurance under each CTA, such as “Cancel anytime.” */
  assurance?: string;
  onContinue?: (product: ScoProduct) => void;
  /** Caller compare rows (values keyed by productId, plus `baselineColumn.key`). Defaults to catalog features. */
  comparisonRows?: PaywallComparisonRow[];
  baselineColumn?: PaywallComparisonBaseline;
  /** `given` keeps the caller’s order. `recommended` leads with the intent’s plan. */
  productOrder?: 'recommended' | 'given';
};

/** Readers who reach the end have compared and read the answers; give them the next step there. */
const ClosingOffer = t0 => {
  const $ = _c(58);
  const {
    assurance,
    children,
    ctaLabel,
    heading,
    intent,
    onContinue,
    product,
    tokens,
    upgrade
  } = t0;
  let t1;
  if ($[0] !== product) {
    t1 = productLabel(product);
    $[0] = product;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  let t2;
  if ($[2] !== product) {
    t2 = productPriceLabel(product);
    $[2] = product;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const price = t2;
  let copy;
  let t3;
  if ($[4] !== ctaLabel || $[5] !== intent || $[6] !== product || $[7] !== tokens || $[8] !== upgrade) {
    copy = productPaywallIntent(product, intent, tokens);
    t3 = productCtaLabel(product, copy, ctaLabel, {
      upgrade
    });
    $[4] = ctaLabel;
    $[5] = intent;
    $[6] = product;
    $[7] = tokens;
    $[8] = upgrade;
    $[9] = copy;
    $[10] = t3;
  } else {
    copy = $[9];
    t3 = $[10];
  }
  const label = t3;
  let t4;
  let t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      base: 6,
      md: 8
    };
    t5 = {
      base: 5,
      md: 3
    };
    t6 = {
      base: "1fr",
      md: "minmax(0, 1fr) auto"
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[14] !== children) {
    t7 = children ? {
      base: "\"copy\" \"action\" \"extra\"",
      md: "\"copy action\" \"extra action\""
    } : {
      base: "\"copy\" \"action\"",
      md: "\"copy action\""
    };
    $[14] = children;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = children ? "end" : "center";
  let t9;
  if ($[16] !== t8) {
    t9 = {
      md: t8
    };
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      base: "lg",
      md: "xl"
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = heading || (upgrade ? `Ready to move up to ${name}?` : `Ready for ${name}?`);
  let t12;
  if ($[19] !== t11) {
    t12 = <Heading as="h2" id="paywall-closing-heading" size={t10} textWrap="balance">{t11}</Heading>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== copy || $[22] !== product) {
    t13 = productDescription(product) || copy.supporting;
    $[21] = copy;
    $[22] = product;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <Text color="fg.muted" textStyle="sm" textWrap="pretty">{t13}</Text>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t12 || $[27] !== t14 || $[28] !== t9) {
    t15 = <Stack alignSelf={t9} gap={1.5} gridArea="copy" maxW="36rem">{t12}{t14}</Stack>;
    $[26] = t12;
    $[27] = t14;
    $[28] = t9;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      md: "17rem"
    };
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = `${label}, ${price}`;
  let t18;
  if ($[31] !== onContinue || $[32] !== product) {
    t18 = onContinue ? () => onContinue(product) : undefined;
    $[31] = onContinue;
    $[32] = product;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== label) {
    t19 = <Text>{label}</Text>;
    $[34] = label;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== price) {
    t20 = <Text fontWeight="normal" textStyle="xs">{price}</Text>;
    $[36] = price;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t19 || $[39] !== t20) {
    t21 = <Stack align="center" gap={0}>{t19}{t20}</Stack>;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== product || $[42] !== t17 || $[43] !== t18 || $[44] !== t21) {
    t22 = <PlanChoiceButton aria-label={t17} h="auto" product={product} py={3} variant="cta" whiteSpace="normal" onChoose={t18}>{t21}</PlanChoiceButton>;
    $[41] = product;
    $[42] = t17;
    $[43] = t18;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== assurance) {
    t23 = assurance?.trim() ? <Text color="fg.muted" textAlign="center" textStyle="xs">{assurance.trim()}</Text> : null;
    $[46] = assurance;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t22 || $[49] !== t23) {
    t24 = <Stack gap={2} gridArea="action" minW={t16}>{t22}{t23}</Stack>;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== children) {
    t25 = children ? <Box alignSelf={{
      md: "start"
    }} gridArea="extra" justifySelf={{
      base: "center",
      md: "start"
    }}>{children}</Box> : null;
    $[51] = children;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t15 || $[54] !== t24 || $[55] !== t25 || $[56] !== t7) {
    t26 = <Grid alignItems="center" aria-labelledby="paywall-closing-heading" as="section" bg="colorPalette.card/50" borderColor="orange.solid" borderRadius="lg" borderWidth="1px" columnGap={10} p={t4} rowGap={t5} templateColumns={t6} templateAreas={t7}>{t15}{t24}{t25}</Grid>;
    $[53] = t15;
    $[54] = t24;
    $[55] = t25;
    $[56] = t7;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  return t26;
};

/**
 * Full-page density modeled on /advisors/upgrade: plan cards, a compare, and short answers.
 * Presentational. The story supplies products and answers. This does not read the user or start checkout.
 */
export const PaywallPage = t0 => {
  const $ = _c(47);
  const {
    align: t1,
    answers: t2,
    assurance,
    baselineColumn,
    comparisonRows,
    cardAppearance,
    cardsFooter,
    closingExtra,
    controls,
    ctaLabels,
    currentProductIds,
    currentTermByProductId,
    eligibleProductIds,
    intent,
    onContinue,
    productOrder: t3,
    products,
    showHero: t4,
    tokens
  } = t0;
  const align = t1 === undefined ? "start" : t1;
  let t5;
  if ($[0] !== align || $[1] !== assurance || $[2] !== baselineColumn || $[3] !== cardAppearance || $[4] !== cardsFooter || $[5] !== closingExtra || $[6] !== comparisonRows || $[7] !== controls || $[8] !== ctaLabels || $[9] !== currentProductIds || $[10] !== currentTermByProductId || $[11] !== eligibleProductIds || $[12] !== intent || $[13] !== onContinue || $[14] !== products || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== tokens) {
    const answers = t2 === undefined ? [] : t2;
    const productOrder = t3 === undefined ? "recommended" : t3;
    const showHero = t4 === undefined ? true : t4;
    const questions = answers.filter(_temp);
    const resolved = resolvePaywallIntent(intent, tokens);
    const offerOptions = {
      currentProductIds,
      currentTermByProductId,
      eligibleProductIds
    };
    const closing = orderProductsForIntent(products, resolved).find(product => {
      const state = productOfferState(product, resolved, offerOptions);
      return state.showAction && !state.current && productCanContinue(product);
    });
    const closingState = closing ? productOfferState(closing, resolved, offerOptions) : null;
    const closingTerm = closing?.effectiveTermId || closing?.termId || "";
    const closingTermLabel = closingState?.termChange && closingTerm ? billingTermChangeLabel(closingState.termChange, closingTerm) : null;
    let t6;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        base: 12,
        md: 16
      };
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== align || $[22] !== assurance || $[23] !== cardAppearance || $[24] !== cardsFooter || $[25] !== controls || $[26] !== ctaLabels || $[27] !== currentProductIds || $[28] !== currentTermByProductId || $[29] !== eligibleProductIds || $[30] !== intent || $[31] !== onContinue || $[32] !== productOrder || $[33] !== products || $[34] !== showHero || $[35] !== tokens) {
      t7 = <PaywallProductGrid align={align} assurance={assurance} cardAppearance={cardAppearance} cardsFooter={cardsFooter} controls={controls} ctaLabels={ctaLabels} currentProductIds={currentProductIds} currentTermByProductId={currentTermByProductId} eligibleProductIds={eligibleProductIds} intent={intent} productOrder={productOrder} products={products} showHero={showHero} showPlanSummary={false} tokens={tokens} onContinue={onContinue} />;
      $[21] = align;
      $[22] = assurance;
      $[23] = cardAppearance;
      $[24] = cardsFooter;
      $[25] = controls;
      $[26] = ctaLabels;
      $[27] = currentProductIds;
      $[28] = currentTermByProductId;
      $[29] = eligibleProductIds;
      $[30] = intent;
      $[31] = onContinue;
      $[32] = productOrder;
      $[33] = products;
      $[34] = showHero;
      $[35] = tokens;
      $[36] = t7;
    } else {
      t7 = $[36];
    }
    let t8;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        base: 3,
        md: 5
      };
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    let t9;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Heading as="h2" id="paywall-compare-heading" size={{
        base: "lg",
        md: "xl"
      }}>Compare plans</Heading>;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    const t10 = cardAppearance === "quiet" ? "quiet" : "standard";
    let t11;
    if ($[39] !== baselineColumn || $[40] !== comparisonRows || $[41] !== intent || $[42] !== productOrder || $[43] !== products || $[44] !== t10 || $[45] !== tokens) {
      t11 = <Stack aria-labelledby="paywall-compare-heading" as="section" gap={t8}>{t9}<ProductDetailComparisonTable appearance={t10} baselineColumn={baselineColumn} comparisonRows={comparisonRows} intent={intent} productOrder={productOrder} products={products} showIntro={false} tokens={tokens} /></Stack>;
      $[39] = baselineColumn;
      $[40] = comparisonRows;
      $[41] = intent;
      $[42] = productOrder;
      $[43] = products;
      $[44] = t10;
      $[45] = tokens;
      $[46] = t11;
    } else {
      t11 = $[46];
    }
    t5 = <Stack gap={t6} maxW="5xl" mx="auto" w="full">{t7}{t11}{questions.length > 0 ? <Stack aria-labelledby="paywall-answers-heading" as="section" gap={{
        base: 3,
        md: 5
      }}><Heading as="h2" id="paywall-answers-heading" size={{
          base: "lg",
          md: "xl"
        }}>Common questions</Heading><Stack borderTopColor="border" borderTopWidth="1px" gap={0}>{questions.map(_temp2)}</Stack></Stack> : null}{closing ? <ClosingOffer assurance={assurance} ctaLabel={closing.productId && ctaLabels?.[closing.productId] || closingTermLabel || undefined} intent={intent} product={closing} tokens={tokens} upgrade={Boolean(currentProductIds?.length) && !closingTermLabel} heading={closingTermLabel && !(closing.productId && ctaLabels?.[closing.productId]) ? `Ready to ${closingTermLabel.charAt(0).toLowerCase()}${closingTermLabel.slice(1)}?` : undefined} onContinue={onContinue}>{closingExtra}</ClosingOffer> : null}</Stack>;
    $[0] = align;
    $[1] = assurance;
    $[2] = baselineColumn;
    $[3] = cardAppearance;
    $[4] = cardsFooter;
    $[5] = closingExtra;
    $[6] = comparisonRows;
    $[7] = controls;
    $[8] = ctaLabels;
    $[9] = currentProductIds;
    $[10] = currentTermByProductId;
    $[11] = eligibleProductIds;
    $[12] = intent;
    $[13] = onContinue;
    $[14] = products;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = tokens;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
};
function _temp(answer) {
  return answer.question.trim() && answer.answer.trim();
}
function _temp2(answer_0) {
  return <Grid key={answer_0.question} borderBottomColor="border" borderBottomWidth="1px" columnGap={10} py={{
    base: 4,
    md: 5
  }} rowGap={1.5} templateColumns={{
    base: "1fr",
    md: "minmax(13rem, 0.8fr) minmax(0, 1.2fr)"
  }}><Heading as="h3" size="sm" textWrap="balance">{answer_0.question}</Heading><Text color="fg.muted" maxW="65ch" textStyle="sm" textWrap="pretty">{answer_0.answer}</Text></Grid>;
}